'use client'

import Link from 'next/link'
import React, { useActionState, useState } from 'react'

import { accedi, registrati, reinviaVerifica, type StatoForm } from '../actions'
import { PASSWORD_MIN } from '../regole'
import { ErroreCampo, Esito } from './Feedback'

const iniziale: StatoForm = { stato: 'idle' }

export type ModoAccount = 'accedi' | 'registrati'

/**
 * The prototype's account panel (`.accounttabs` + `.accountform`) as a page: login and
 * registration share the tabs, each form posts to its server action.
 */
export const AccountForms: React.FC<{
  modo: ModoAccount
  next?: string
  avviso?: string
  /** `/<slug>` of the privacy notice, linked from the registration consent. */
  privacy?: string
}> = ({ modo: modoIniziale, next, avviso, privacy }) => {
  const [modo, setModo] = useState<ModoAccount>(modoIniziale)
  const registrazione = modo === 'registrati'

  return (
    <>
      <span className="eyebrow">AREA CLIENTE</span>
      <h1 className="modalheading">
        {registrazione ? 'Crea il tuo account' : 'Accedi al tuo account'}
      </h1>
      <p>Salva i tuoi veicoli e ritrova le tinte acquistate.</p>
      <div className="accounttabs" role="group" aria-label="Accedi o registrati">
        <button aria-pressed={!registrazione} onClick={() => setModo('accedi')} type="button">
          Accedi
        </button>
        <button aria-pressed={registrazione} onClick={() => setModo('registrati')} type="button">
          Registrati
        </button>
      </div>
      {avviso && !registrazione && (
        <p className="accountfeedback" role="status">
          {avviso}
        </p>
      )}
      {registrazione ? <FormRegistrazione privacy={privacy} /> : <FormAccesso next={next} />}
    </>
  )
}

/** Login form, also used inline by the checkout (`next=/cassa`). */
export const FormAccesso: React.FC<{ next?: string }> = ({ next }) => {
  const [stato, azione, inCorso] = useActionState(accedi, iniziale)

  return (
    <form action={azione} className="accountform" aria-label="Accedi">
      {next && <input name="next" type="hidden" value={next} />}
      <label>
        Email
        <input
          autoComplete="email"
          defaultValue={stato.valori?.email}
          name="email"
          required
          type="email"
        />
      </label>
      <label>
        Password
        <input autoComplete="current-password" name="password" required type="password" />
      </label>
      <Link className="textlink" href="/account/password-dimenticata">
        Password dimenticata?
      </Link>
      <button className="primary" disabled={inCorso} type="submit">
        {inCorso ? 'Accesso in corso…' : 'Accedi →'}
      </button>
      <Esito stato={stato} />
      {stato.nonVerificato && stato.valori?.email && (
        <ReinviaVerifica email={stato.valori.email} key={stato.valori.email} />
      )}
    </form>
  )
}

/**
 * Shown after a login refused for an unconfirmed email. Its button posts to its own action
 * (`formAction`), with just the email: a form can't be nested inside the login form.
 */
const ReinviaVerifica: React.FC<{ email: string }> = ({ email }) => {
  // The address of the refused login, not whatever the email input holds now.
  const [stato, azione, inCorso] = useActionState(async (prev: StatoForm) => {
    const formData = new FormData()
    formData.set('email', email)
    return reinviaVerifica(prev, formData)
  }, iniziale)

  if (stato.stato === 'ok') {
    return (
      <p className="accountfeedback" role="status">
        {stato.messaggio}
      </p>
    )
  }
  return (
    <>
      <button
        className="secondary"
        disabled={inCorso}
        formAction={azione}
        formNoValidate
        type="submit"
      >
        {inCorso ? 'Invio in corso…' : 'Reinvia email di conferma'}
      </button>
      {stato.stato === 'errore' && <Esito stato={stato} />}
    </>
  )
}

const FormRegistrazione: React.FC<{ privacy?: string }> = ({ privacy }) => {
  // `risposta` counts the server's answers: the form is keyed on it and remounts with the
  // returned values. React resets a form after each action, and a <select> would otherwise
  // reset to its first-render option (Privato) while the company fields stay on screen.
  const [stato, azione, inCorso] = useActionState(
    async (prev: StatoForm & { risposta?: number }, formData: FormData) => ({
      ...(await registrati(prev, formData)),
      risposta: (prev.risposta ?? 0) + 1,
    }),
    { ...iniziale, risposta: 0 },
  )
  const valori = stato.valori ?? {}
  const [tipo, setTipo] = useState(valori.tipoCliente ?? 'privato')
  const azienda = tipo === 'azienda'
  const campi = stato.campi ?? {}

  if (stato.stato === 'ok') {
    return (
      <div className="accountform">
        <p className="accountfeedback" role="status">
          {stato.messaggio}
        </p>
        <p className="modalnote">
          Non trovi l’email? Controlla lo spam. Il link di conferma resta valido finché non lo usi.
        </p>
      </div>
    )
  }

  return (
    <form action={azione} aria-label="Registrati" className="accountform" key={stato.risposta}>
      <label>
        Tipo di cliente
        <select
          defaultValue={valori.tipoCliente ?? 'privato'}
          name="tipoCliente"
          onChange={(e) => setTipo(e.target.value)}
        >
          <option value="privato">Privato</option>
          <option value="azienda">Professionista / Azienda</option>
        </select>
      </label>
      <div className="accountcolumns">
        <label>
          Nome
          <input
            autoComplete="given-name"
            defaultValue={valori.nome}
            maxLength={80}
            name="nome"
            required
          />
          <ErroreCampo messaggio={campi.nome} />
        </label>
        <label>
          Cognome
          <input
            autoComplete="family-name"
            defaultValue={valori.cognome}
            maxLength={80}
            name="cognome"
            required
          />
          <ErroreCampo messaggio={campi.cognome} />
        </label>
      </div>
      {azienda && (
        <div id="companyFields">
          <label>
            Ragione sociale
            <input
              autoComplete="organization"
              defaultValue={valori.ragioneSociale}
              maxLength={160}
              name="ragioneSociale"
              required
            />
            <ErroreCampo messaggio={campi.ragioneSociale} />
          </label>
          <div className="accountcolumns">
            <label>
              Partita IVA
              <input
                defaultValue={valori.partitaIva}
                inputMode="numeric"
                maxLength={16}
                name="partitaIva"
                required
              />
              <ErroreCampo messaggio={campi.partitaIva} />
            </label>
            <label>
              Codice SDI <span>(facoltativo)</span>
              <input
                defaultValue={valori.codiceSdi}
                maxLength={7}
                name="codiceSdi"
                style={{ textTransform: 'uppercase' }}
              />
              <ErroreCampo messaggio={campi.codiceSdi} />
            </label>
          </div>
          <label>
            PEC <span>(facoltativa)</span>
            <input defaultValue={valori.pec} name="pec" type="email" />
            <ErroreCampo messaggio={campi.pec} />
          </label>
        </div>
      )}
      <div className="accountcolumns">
        <label>
          Codice fiscale <span>(facoltativo)</span>
          <input
            defaultValue={valori.codiceFiscale}
            maxLength={16}
            name="codiceFiscale"
            style={{ textTransform: 'uppercase' }}
          />
          <ErroreCampo messaggio={campi.codiceFiscale} />
        </label>
        <label>
          Telefono <span>(facoltativo)</span>
          <input autoComplete="tel" defaultValue={valori.telefono} name="telefono" type="tel" />
          <ErroreCampo messaggio={campi.telefono} />
        </label>
      </div>
      <label>
        Email
        <input
          autoComplete="email"
          defaultValue={valori.email}
          name="email"
          required
          type="email"
        />
        <ErroreCampo messaggio={campi.email} />
      </label>
      <label>
        Password
        <input
          autoComplete="new-password"
          minLength={PASSWORD_MIN}
          name="password"
          required
          type="password"
        />
        <ErroreCampo messaggio={campi.password} />
      </label>
      <small>Usa almeno {PASSWORD_MIN} caratteri.</small>
      <label className="accountconsent">
        <input defaultChecked={valori.privacy === 'on'} name="privacy" required type="checkbox" />{' '}
        Ho letto l’
        {privacy ? (
          <Link className="textlink" href={privacy} target="_blank">
            informativa privacy
          </Link>
        ) : (
          'informativa privacy'
        )}{' '}
        e acconsento al trattamento dei miei dati per la gestione dell’account
        {!privacy && <span> (testo in pubblicazione)</span>}
        <ErroreCampo messaggio={campi.consensoPrivacy} />
      </label>
      <label className="accountconsent">
        <input defaultChecked={valori.marketing === 'on'} name="marketing" type="checkbox" /> Voglio
        ricevere offerte e novità via email <span>(facoltativo, revocabile)</span>
      </label>
      <label className="accountconsent">
        <input defaultChecked={valori.collegaOrdini !== ''} name="collegaOrdini" type="checkbox" />{' '}
        Aggiungi al mio account gli ordini già fatti come ospite con questa email{' '}
        <span>(dopo la conferma dell’indirizzo)</span>
      </label>
      <button className="primary" disabled={inCorso} type="submit">
        {inCorso ? 'Creazione in corso…' : 'Crea account →'}
      </button>
      <Esito stato={stato} />
    </form>
  )
}
